/**
 * RouteCreateForm integration boundary for legacy/successor step advance + submit.
 * Delegates rules to cp060RouteCreateStateMachine (no duplicate validation logic).
 */
import type { SuccessorRouteCreateContext } from './cp060RouteCreateFlowHelpers';
import { assertApplicationSelectedBeforeCargo } from './cp060RouteCreateFlowHelpers';
import {
  buildApplicationChangeResetPatch,
  buildPhysicalSourceClearPatch
} from '../../../features/routeCreatePhysicalSource/routeCreateSourceStateReset';
import {
  buildRouteCreateFlowStateFromForm,
  validateLegacyRouteCreateFlowForCargo
} from './cp060RouteCreateStateMachine';
import {
  evaluateLegacyWarehouseCargoStepAccess,
  isRestoredWarehouseLoadingSource,
  type PendingRestoredWarehouseVerification
} from './legacyRestoredWarehouseVerification';

export const LEGACY_CARGO_STEP_INDEX = 3;

export type LegacyRouteCreateFormSlice = {
  applicationId?: unknown;
  loadingPointType?: unknown;
  loadingPointId?: unknown;
  cargoIds?: unknown;
};

export function shouldRunLegacyCargoGateOnStepAdvance(
  currentStep: number,
  groupUiEnabled: boolean
): boolean {
  return !groupUiEnabled && (currentStep === 0 || currentStep === 2);
}

export function evaluateLegacyRouteCreateCargoGate(params: {
  formSlice: LegacyRouteCreateFormSlice;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
}): { ok: true } | { ok: false; message: string } {
  return validateLegacyRouteCreateFlowForCargo(
    buildRouteCreateFlowStateFromForm({
      applicationId: params.formSlice.applicationId,
      loadingPointType: params.formSlice.loadingPointType,
      loadingPointId: params.formSlice.loadingPointId,
      editingRouteId: params.editingRouteId,
      successorContext: params.successorContext
    })
  );
}

export type LegacyWarehouseVerificationNavState = {
  pendingWarehouseVerification: PendingRestoredWarehouseVerification | null;
  confirmedSourceKey: string | null;
};

/** Same gate as handleFinish early return before POST /routes. */
export function evaluateLegacyRouteCreateSubmitGate(params: {
  formSlice: LegacyRouteCreateFormSlice;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
  groupUiEnabled?: boolean;
} & Partial<LegacyWarehouseVerificationNavState>): { ok: true } | { ok: false; message: string; redirectStep: 0 } {
  const gate = evaluateLegacyRouteCreateCargoGate(params);
  if (!gate.ok) {
    return { ok: false, message: gate.message, redirectStep: 0 };
  }
  if (params.groupUiEnabled) {
    return { ok: true };
  }
  const warehouseStep = evaluateLegacyWarehouseCargoStepAccess({
    groupUiEnabled: false,
    formSlice: params.formSlice,
    pendingWarehouseVerification: params.pendingWarehouseVerification ?? null,
    confirmedSourceKey: params.confirmedSourceKey ?? null
  });
  if (!warehouseStep.ok) {
    return { ok: false, message: warehouseStep.message, redirectStep: 0 };
  }
  return { ok: true };
}

/**
 * Mirrors RouteCreateForm handleNext after field validation — blocks Cargo step until warehouse verified.
 */
export function evaluateLegacyStepAdvanceGate(params: {
  groupUiEnabled: boolean;
  currentStep: number;
  formSlice: LegacyRouteCreateFormSlice;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
} & LegacyWarehouseVerificationNavState): { ok: true; nextStep: number } | { ok: false; message: string } {
  const nextStep = params.currentStep + 1;

  if (
    !params.groupUiEnabled &&
    shouldRunLegacyCargoGateOnStepAdvance(params.currentStep, params.groupUiEnabled)
  ) {
    const cargoGate = evaluateLegacyRouteCreateCargoGate({
      formSlice: params.formSlice,
      editingRouteId: params.editingRouteId,
      successorContext: params.successorContext
    });
    if (!cargoGate.ok) {
      return { ok: false, message: cargoGate.message };
    }
  }

  if (!params.groupUiEnabled && nextStep >= LEGACY_CARGO_STEP_INDEX) {
    const warehouseStep = evaluateLegacyWarehouseCargoStepAccess({
      groupUiEnabled: false,
      formSlice: params.formSlice,
      pendingWarehouseVerification: params.pendingWarehouseVerification,
      confirmedSourceKey: params.confirmedSourceKey
    });
    if (!warehouseStep.ok) {
      return { ok: false, message: warehouseStep.message };
    }
  }

  return { ok: true, nextStep };
}

/** Acceptance helper: one handleNext click after validation succeeded. */
export function simulateLegacyHandleNext(params: {
  groupUiEnabled: boolean;
  currentStep: number;
  formSlice: LegacyRouteCreateFormSlice;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
} & LegacyWarehouseVerificationNavState): {
  advanced: boolean;
  currentStep: number;
  message?: string;
} {
  if (params.currentStep >= LEGACY_CARGO_STEP_INDEX) {
    return { advanced: false, currentStep: params.currentStep };
  }
  const gate = evaluateLegacyStepAdvanceGate(params);
  if (!gate.ok) {
    return { advanced: false, currentStep: params.currentStep, message: gate.message };
  }
  return { advanced: true, currentStep: gate.nextStep };
}

export function computeLegacyLoadingSourceKey(
  loadingPointType?: unknown,
  loadingPointId?: unknown
): string {
  return `${String(loadingPointType ?? '')}|${String(loadingPointId ?? '')}`;
}

export function shouldClearCargoOnLoadingSourceChange(
  previousKey: string | null,
  nextKey: string
): boolean {
  if (previousKey === null) return false;
  return previousKey !== nextKey;
}

/** Mirrors handleLegacyApplicationChange + application-id effect in RouteCreateForm. */
export function applyLegacyApplicationChangeInForm(
  nextApplicationId: number,
  currentForm: LegacyRouteCreateFormSlice & Record<string, unknown>
): LegacyRouteCreateFormSlice & Record<string, unknown> {
  const reset = buildApplicationChangeResetPatch(nextApplicationId);
  const clearSource = buildPhysicalSourceClearPatch();
  return {
    ...currentForm,
    ...reset,
    ...clearSource,
    applicationId: nextApplicationId,
    cargoIds: []
  };
}

/** Simulates loadingSourceKey effect clearing cargo when source changes. */
export function applyLegacyLoadingSourceChangeInForm(
  currentForm: LegacyRouteCreateFormSlice & Record<string, unknown>,
  next: { loadingPointType?: unknown; loadingPointId?: unknown }
): LegacyRouteCreateFormSlice & Record<string, unknown> {
  return {
    ...currentForm,
    ...next,
    cargoIds: []
  };
}

/**
 * handleFinish must not reach createRoute when this returns blocked.
 * (Used by component-level acceptance tests.)
 */
export type LegacyDraftRestoreInput = {
  draftStep?: unknown;
  draftValues: LegacyRouteCreateFormSlice & Record<string, unknown>;
  groupUiEnabled: boolean;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
};

export type LegacyDraftRestoreResult = {
  values: Record<string, unknown>;
  step: number;
  /** When false, GET /routes/cargo/available must stay skipped (cp059CargoQueryParams null). */
  cargoQueryAllowed: boolean;
  clearedStaleSource: boolean;
  /** Warehouse restored structurally — must match physical-sources before Cargo. */
  pendingWarehouseVerification?: PendingRestoredWarehouseVerification;
};

/**
 * After localStorage draft restore — block Cargo step until Application + Physical Source valid.
 */
export function normalizeLegacyRouteCreateDraftRestore(
  input: LegacyDraftRestoreInput
): LegacyDraftRestoreResult {
  const values: Record<string, unknown> = {
    ...input.draftValues,
    cargoIds: []
  };

  if (input.groupUiEnabled) {
    const step = coerceDraftStep(input.draftStep);
    return { values, step, cargoQueryAllowed: true, clearedStaleSource: false };
  }

  const gate = evaluateLegacyRouteCreateCargoGate({
    formSlice: values,
    editingRouteId: input.editingRouteId,
    successorContext: input.successorContext
  });

  if (gate.ok) {
    const requestedStep = coerceDraftStep(input.draftStep);
    if (isRestoredWarehouseLoadingSource(values)) {
      const appId = Number(values.applicationId);
      const warehouseId = Number(values.loadingPointId);
      return {
        values,
        step: requestedStep >= LEGACY_CARGO_STEP_INDEX ? 0 : requestedStep,
        cargoQueryAllowed: false,
        clearedStaleSource: false,
        pendingWarehouseVerification: {
          queryApplicationId: appId,
          warehouseId,
          stepAfterVerification: requestedStep
        }
      };
    }
    return {
      values,
      step: requestedStep,
      cargoQueryAllowed: true,
      clearedStaleSource: false
    };
  }

  let clearedStaleSource = false;
  const hasApplication = assertApplicationSelectedBeforeCargo(
    values.applicationId as number | null | undefined
  );

  if (!hasApplication) {
    Object.assign(values, buildPhysicalSourceClearPatch());
    clearedStaleSource = true;
  } else if (
    values.loadingPointType != null ||
    values.loadingPointId != null
  ) {
    Object.assign(values, buildPhysicalSourceClearPatch());
    clearedStaleSource = true;
  }

  const requestedStep = coerceDraftStep(input.draftStep);
  const mustBlockCargoStep = requestedStep >= LEGACY_CARGO_STEP_INDEX || !gate.ok;

  return {
    values,
    step: mustBlockCargoStep ? 0 : requestedStep,
    cargoQueryAllowed: false,
    clearedStaleSource
  };
}

function coerceDraftStep(raw: unknown): number {
  if (!Number.isInteger(raw)) return 0;
  const n = raw as number;
  return Math.min(LEGACY_CARGO_STEP_INDEX, Math.max(0, n));
}

export async function runLegacySubmitWithCreateRouteGuard(params: {
  formSlice: LegacyRouteCreateFormSlice;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
  createRoute: (body: unknown) => Promise<unknown>;
} & Partial<LegacyWarehouseVerificationNavState>): Promise<{ posted: boolean; message?: string }> {
  const gate = evaluateLegacyRouteCreateSubmitGate({
    formSlice: params.formSlice,
    editingRouteId: params.editingRouteId,
    successorContext: params.successorContext,
    pendingWarehouseVerification: params.pendingWarehouseVerification ?? null,
    confirmedSourceKey: params.confirmedSourceKey ?? null
  });
  if (!gate.ok) {
    return { posted: false, message: gate.message };
  }
  await params.createRoute({});
  return { posted: true };
}
